import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';

import { Provider } from 'react-redux';
import store from 'store/store';

import App from './App';
import { MyInvestments, Orders, Survey } from './components';
import ProjectStages from './components/pages/detailed-project';
import { ProjectChat } from './components/pages/detailed-project/ProjectChat';
import ProjectDocuments from './components/pages/detailed-project/ProjectDocuments';
import ProjectEconomicAnalysis from './components/pages/detailed-project/ProjectEconomicAnalysis';
import ProjectLegalAnalysis from './components/pages/detailed-project/ProjectLegalAnalysis';
import ProjectStrategy from './components/pages/detailed-project/ProjectStrategy';
import { AuthProtectedRouteGroup } from './components/routing';
import sentryConfig from './configs/sentry';
import { useScrollToTop } from './hooks';
import {
  AgentDocuments,
  ArchivedProjects,
  Auth,
  Control,
  CreateOffer,
  Dashboard,
  Deposit,
  DetailedProject,
  DocumentsToProject,
  Enter,
  ForgotPassword,
  Identification,
  InvestmentConfirmation,
  Logout,
  MoneyLayout,
  MyDocumentsLayout,
  MyProjects,
  NotFound,
  Notifications,
  OrderConfirmation,
  PersonalDataPolicy,
  Profile,
  Projects,
  RedirectAfterAuth,
  ReferralProgram,
  RegistrationRules,
  Rules,
  Transactions,
  VerificationEmail,
  Withdraw,
} from './views';
import OfferConfirmation from './views/dashboard/lender/investment/offer/OfferConfirmation';
import PrimaryMarket from './views/dashboard/lender/investment/projects/PrimaryMarket';
import PurchaseOffer from './views/dashboard/lender/investment/purchase';
import PurchaseOfferConfirmation from './views/dashboard/lender/investment/purchase/PurchaseConfirmation';
import { AccountDetailsPage } from './views/dashboard/lender/stats/AccountDetails';
import { PackageDetailsPage } from './views/dashboard/lender/stats/AccountDetails/PackageDetails';
import MyArchive from './views/dashboard/lender/stats/MyArchive';
import MyMoneyV2 from './views/dashboard/lender/stats/MyMoneyV2';
import { MyMoneyHome } from './views/dashboard/lender/stats/MyMoneyV2/MyMoneyHome';
import { AboutUs, FAQ, HowItWorks, News, Platform } from './views/public';
import Contacts from './views/public/Contacts';
import { HowItWorksNavigation } from './views/public/HowItWorksNavigation/HowItWorksNavigation';
import Public from './views/public/Public/Public';
import { VerifyEmail } from './views/verification';

import 'assets/scss/app.scss';

const root = createRoot(document.getElementById('root') as HTMLElement);

Sentry.init({
  dsn: sentryConfig.sentryDsn,
  integrations: [new BrowserTracing()],
  environment: process.env.NODE_ENV,
  denyUrls: [/^chrome-extension:\/\//, /^moz-extension:\/\//, /^safari-web-extension:\/\//],
});

function Index() {
  useScrollToTop({
    exclude: [
      '/dashboard/project/:uuid/legal',
      '/dashboard/project/:uuid/strategy',
      '/dashboard/project/:uuid/economic',
      '/dashboard/project/:uuid/documents',
      '/dashboard/project/:uuid/stages',
      '/dashboard/project/:uuid/chat',
    ],
    behavior: 'smooth',
  });
  return (
    <Routes>
      <Route path="auth" element={<Auth />}>
        <Route path="redirect-after-auth" element={<RedirectAfterAuth />} />
        <Route path="logout" element={<Logout />} />
        <Route path="verification/:verificationCode" element={<VerificationEmail />} />
      </Route>
      <Route path="enter" element={<Enter />} />
      <Route path="forgot-password" element={<ForgotPassword />} />
      <Route path="dashboard" element={<Dashboard />}>
        <Route element={<AuthProtectedRouteGroup />}>
          <Route path="notifications" element={<Notifications />} />
          <Route path="survey/:uuid" element={<Survey />} />
          <Route path="identification" element={<Identification />} />
          <Route path="transactions" element={<Transactions />} />
          <Route path="transaction/:uuid" />
          <Route path="my-money" element={<MyMoneyV2 />}>
            <Route index element={<MyMoneyHome />} />
            <Route
              path="sign-contract"
              element={<Navigate to="/dashboard/my-money?sign-contract" replace />}
            />
            <Route path="account/comfort/package/:packageUuid" element={<PackageDetailsPage />} />
            <Route path="account/:accountType" element={<AccountDetailsPage />} />
          </Route>
          <Route path="my-projects" element={<MyProjects />}>
            <Route index element={<MyInvestments />} />
            <Route path="my-investments" element={<MyInvestments />} />
            <Route path="orders" element={<Orders />} />
          </Route>
          <Route path="my-archive" element={<MyArchive />} />
          <Route element={<MoneyLayout />}>
            <Route path="withdraw" element={<Withdraw />} />
            <Route path="deposit" element={<Deposit />} />
          </Route>
          <Route path="my-documents" element={<MyDocumentsLayout />}>
            <Route path="documents-to-project" element={<DocumentsToProject />} />
            <Route path="platform-documents" element={<AgentDocuments />} />
          </Route>
          <Route path="referral-program" element={<ReferralProgram />} />
          <Route path="market" element={<Projects />}>
            <Route index element={<PrimaryMarket />} />
            <Route path="primary" element={<PrimaryMarket />} />
            {/*<Route path="buyback" element={<SecondaryMarket/>}/>*/}
          </Route>
          <Route path="archived" element={<ArchivedProjects />} />
          <Route path="project/:uuid" element={<DetailedProject />}>
            <Route index element={<ProjectEconomicAnalysis />} />
            <Route path="economic" element={<ProjectEconomicAnalysis />} />
            <Route path="legal" element={<ProjectLegalAnalysis />} />
            <Route path="strategy" element={<ProjectStrategy />} />
            <Route path="documents" element={<ProjectDocuments />} />
            <Route path="stages" element={<ProjectStages />} />
            <Route path="chat" element={<ProjectChat />} />
            <Route path="confirmation/:confirmationUuid" element={<InvestmentConfirmation />} />
            <Route path="order-confirmation/:confirmationUuid" element={<OrderConfirmation />} />
          </Route>
          <Route path="create-offer/:investmentUuid" element={<CreateOffer />}>
            <Route path="confirmation/:confirmationUuid" element={<OfferConfirmation />} />
          </Route>
          <Route path="purchase-offer/:offerUuid" element={<PurchaseOffer />}>
            <Route index element={<ProjectEconomicAnalysis />} />
            <Route path="economic" element={<ProjectEconomicAnalysis />} />
            <Route path="legal" element={<ProjectLegalAnalysis />} />
            <Route path="strategy" element={<ProjectStrategy />} />
            <Route path="documents" element={<ProjectDocuments />} />
            <Route path="stages" element={<ProjectStages />} />
            <Route path="confirmation/:confirmationUuid" element={<PurchaseOfferConfirmation />} />
          </Route>
          <Route path="profile" element={<Profile />}>
            <Route path="control" element={<Control />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Route>
      </Route>
      <Route element={<Public />}>
        <Route path="/" element={<App />} />
        <Route path="/rules_service" element={<Rules />} />
        <Route path="/rules_registration" element={<RegistrationRules />} />
        <Route path="/personal_data_policy" element={<PersonalDataPolicy />} />
        <Route path="/how-it-works" element={<HowItWorksNavigation />}>
          <Route index element={<HowItWorks />} />
          <Route path="faq" element={<FAQ />} />
          <Route path="contacts" element={<Contacts />} />
        </Route>
        <Route path="/platform" element={<Platform />} />
        <Route path="/news" element={<News />} />
        <Route path="/about-us" element={<AboutUs />} />
      </Route>
      <Route path="/verify-email/:verificationCode" element={<VerifyEmail />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

const Root = () => {
  //TODO: ADD FALLBACK COMPONENT
  return (
    <Sentry.ErrorBoundary onError={(error) => Sentry.captureException(error)}>
      <Provider store={store}>
        <BrowserRouter>
          <Index />
        </BrowserRouter>
      </Provider>
    </Sentry.ErrorBoundary>
  );
};

root.render(<Root />);
